import React, { useEffect, useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  User,
  Settings,
  Users,
  Shield,
  Download,
  Loader,
  Save,
} from "lucide-react";
import Modal from "../components/common/CommonModal";
import ContractModal from "./ContractModal";
import ContractBoqTable from "./ContractBoqTable";
import { pdf } from "@react-pdf/renderer";
import PdfViewer from "./ContractQuotationPdfDocument";
import QuotationPdfDocument from "./QuotationPdfDocument";
import EmailPreviewModal from "./EmailPreviewModal";
import { useParams } from "react-router-dom";

const ContractingQuotation = () => {
  const [customerType, setCustomerType] = useState("registered");
  const [activeTab, setActiveTab] = useState("details");
  const [modalType, setModalType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [importView, setImportView] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [leadListModal, setLeadListModal] = useState(false);
  const LeadIdFromParams = useParams();
  console.log("Params from URL", LeadIdFromParams?.leadId);
  const [customerDetails, setCustomerDetails] = useState({
    customerName: "",
    engineer: "",
    contactPerson: "",
    authority: "",
    phone: "",
    email: "",
    address: "",
  });

  const [quotationDetails, setQuotationDetails] = useState({
    jobType: "Renovation",
    enquiryNo: "",
    siteCode: "",
  });

  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : {};
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage.getItem("companyCode") || "";

  const defaultItems = [
    {
      id: "1",
      level: 1,
      label: "CIVIL WORKS",
      qty: 0,
      rate: 0,
      costCode: "",
      margin: 0,
      tenderRate: 0,
    },
    {
      id: "2",
      level: 2,
      label: "Earthworks",
      qty: 0,
      rate: 0,
      costCode: "",
      margin: 0,
      tenderRate: 0,
    },
    {
      id: "3",
      level: 3,
      label: "Site Clearing",
      qty: 1,
      rate: 500,
      costCode: "CW-01",
      margin: 15,
      tenderRate: 575,
    },
  ];

  const [items, setItems] = useState(defaultItems);
  const addDefaultItems = () => setItems(defaultItems);
  const [selectedTemplates, setSelectedTemplates] = useState({
    header: true,
    footer: true,
  });

  const parseAPIResponseToItems = (apiData) => {
    const items = [];
    let itemId = 1;

    // 1. PROJECT OVERVIEW SECTION
    items.push({
      id: `phase-${itemId++}`,
      level: 1,
      label: "PROJECT OVERVIEW",
      qty: 0,
      rate: 0,
      costCode: "",
      margin: 0,
      tenderRate: 0,
    });

    if (apiData.projectDescription) {
      items.push({
        id: `desc-${itemId++}`,
        level: 2,
        label: apiData.projectDescription,
        qty: 0,
        rate: 0,
        costCode: "DESC-001",
        margin: 0,
        tenderRate: 0,
      });
    }

    // 2. BREAKDOWN SECTIONS (Materials, Labour, Certifications, Overheads)
    const breakdownConfigs = [
      {
        key: "materialBreakdown",
        label: "Materials",
        margin: 15,
        prefix: "MAT",
      },
      { key: "labourBreakdown", label: "Labor", margin: 20, prefix: "LAB" },
      {
        key: "certificationBreakdown",
        label: "Certification",
        margin: 10,
        prefix: "CRT",
      },
      {
        key: "overheadBreakdown",
        label: "Overheads",
        margin: 5,
        prefix: "OVH",
      },
    ];

    breakdownConfigs.forEach((config) => {
      const dataArray = apiData[config.key] || [];
      if (dataArray.length > 0) {
        items.push({
          id: `group-${itemId++}`,
          level: 2,
          label: config.label,
          qty: 0,
          rate: 0,
          costCode: "",
          margin: 0,
          tenderRate: 0,
        });

        dataArray.forEach((item, index) => {
          const rate = item.unitCost || 0;
          const qty = item.quantity || 0;
          const tenderRate = rate * (1 + config.margin / 100);

          items.push({
            id: `item-${itemId++}`,
            level: 3,
            label: item.item || item.description || "Unnamed Item",
            qty: qty,
            rate: rate,
            costCode: `${config.prefix}-${String(index + 1).padStart(3, "0")}`,
            margin: config.margin,
            tenderRate: tenderRate,
          });
        });
      }
    });

    // 3. DYNAMIC TEXT SECTIONS (Phases, Terms, Assumptions, etc.)
    const targetKeys = [
      { key: "manufacturingPhases", label: "MANUFACTURING PHASES" },
      { key: "workPackages", label: "WORK PACKAGES" },
      { key: "deliveryTimeline", label: "DELIVERY TIMELINE" },
      { key: "paymentTerms", label: "PAYMENT TERMS" },
      { key: "assumptions", label: "ASSUMPTIONS" },
      { key: "exclusions", label: "EXCLUSIONS" },
    ];

    targetKeys.forEach((target) => {
      const dataValue = apiData[target.key];

      if (
        dataValue &&
        (Array.isArray(dataValue) || typeof dataValue === "string")
      ) {
        items.push({
          id: `dynamic-h-${itemId++}`,
          level: 1,
          label: target.label,
          qty: 0,
          rate: 0,
          costCode: "",
          margin: 0,
          tenderRate: 0,
        });

        if (Array.isArray(dataValue)) {
          dataValue.forEach((val, index) => {
            const subLabel =
              typeof val === "string"
                ? val
                : val.item || val.label || `Item ${index + 1}`;
            items.push({
              id: `dynamic-sub-${itemId++}`,
              level: 2,
              label: subLabel,
              qty: val.quantity || 0,
              rate: val.unitCost || 0,
              costCode: `${target.key.substring(0, 3).toUpperCase()}-${String(index + 1).padStart(3, "0")}`,
              margin: 0,
              tenderRate: val.unitCost || 0,
            });
          });
        } else {
          items.push({
            id: `dynamic-sub-${itemId++}`,
            level: 2,
            label: dataValue,
            qty: 0,
            rate: 0,
            costCode: `${target.key.substring(0, 3).toUpperCase()}-001`,
            margin: 0,
            tenderRate: 0,
          });
        }
      }
    });

    return items;
  };

  const [isLoading, setIsLoading] = useState(false);
  const fetchQuotationData = async (leadId) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/ManufacturingQuotation/generate/${COMPANY_CODE}/${leadId}/${DEPARTMENT}`,
        // https://localhost:7063/api/ManufacturingQuotation/generate/SUNRISEWEB/1681/MAIN
      );

      if (!response.ok) {
        throw new Error("Failed to fetch quotation data");
      }

      const data = await response.json();

      const parsedItems = parseAPIResponseToItems(data);
      setItems(parsedItems);
      setIsLoading(false);

      return data;
    } catch (error) {
      console.error("Error fetching quotation:", error);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (LeadIdFromParams?.leadId) {
      fetchQuotationData(LeadIdFromParams?.leadId);
    }
  }, [LeadIdFromParams?.leadId]);

  const handleAIRephrase = async (index) => {
    const updatedItems = [...items];
    updatedItems[index].isRephrasing = true;
    setItems(updatedItems);

    try {
      const originalText = updatedItems[index].label;
      const rephrasedText = await `https://cubixweberp.com:213`(originalText);

      updatedItems[index].label = rephrasedText;
    } catch (error) {
      console.error("AI Error:", error);
    } finally {
      updatedItems[index].isRephrasing = false;
      setItems([...updatedItems]);
    }
  };

  // PDF Generation States
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (showPdfPreview) {
      generatePdf();
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [showPdfPreview]);

  const generatePdf = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      // Pass the DATA to the BLUEPRINT component
      const blob = await pdf(
        <QuotationPdfDocument
          items={items}
          customerDetails={customerDetails}
          quotationDetails={quotationDetails}
          selectedTemplates={selectedTemplates}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error("PDF Generation Error:", err);
      setError("Failed to generate PDF structure.");
    } finally {
      setIsGenerating(false);
    }
  };

  const calculateTotals = () => {
    let actualTotal = 0;
    let tenderTotal = 0;

    items.forEach((item) => {
      if (item.level === 3) {
        actualTotal += item.qty * item.rate;
        tenderTotal += item.qty * item.tenderRate;
      }
    });

    return {
      actualTotal,
      tenderTotal,
      profit: tenderTotal - actualTotal,
    };
  };

  // Save quotation data to JSON
  const saveQuotationData = () => {
    const totals = calculateTotals();
    const quotationData = {
      timestamp: new Date().toISOString(),
      customerType,
      customerDetails,
      quotationDetails,
      items,
      selectedTemplates,
      totals,
      metadata: {
        itemCount: items.length,
        level3Count: items.filter((item) => item.level === 3).length,
        generatedBy: "Contracting Quotation System",
        version: "1.0",
      },
    };

    // Save to localStorage
    const savedQuotations = JSON.parse(
      localStorage.getItem("quotations") || "[]",
    );
    savedQuotations.push(quotationData);
    localStorage.setItem("quotations", JSON.stringify(savedQuotations));

    // Also save the latest quotation separately for quick access
    localStorage.setItem("latestQuotation", JSON.stringify(quotationData));

    // Create a downloadable JSON file
    const dataStr = JSON.stringify(quotationData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Quotation_${quotationDetails.enquiryNo || "Draft"}_${new Date().toISOString().split("T")[0]}.json`;

    // Auto-download the JSON file when saving
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    console.log("Quotation data saved:", quotationData);
    alert("Quotation data saved successfully!");

    return quotationData;
  };

  const handleDownload = async () => {
    try {
      // Save JSON data first
      saveQuotationData();

      // Then download PDF
      const blob = await pdf(
        <PdfViewer
          items={items}
          customerDetails={customerDetails}
          quotationDetails={quotationDetails}
          selectedTemplates={selectedTemplates}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Quotation_${quotationDetails.enquiryNo || "Draft"}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert("Failed to download PDF. Please try again.");
    }
  };

  const handlePrint = () => {
    // Save data to JSON before printing
    saveQuotationData();

    if (pdfUrl) {
      const printWindow = window.open(pdfUrl);
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      }
    }
  };

  const handleUpdateItem = (index, updatedFields) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], ...updatedFields };
    setItems(updatedItems);
  };

  const handleSaveQuotation = () => {
    if (items.length === 0) {
      alert("Please add items to the quotation before saving.");
      return;
    }

    setShowPdfPreview(true);
  };

  const totals = calculateTotals();

  return (
    <div className="p-4 bg-gray-50 ">
      <div className="mx-auto grid grid-cols-12 gap-6 items-start">
        <div className="col-span-3 space-y-4 sticky top-4">
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab("details")}
                className={`flex-1 py-3 text-[11px] font-bold uppercase tracking-tighter transition-all ${activeTab === "details" ? "bg-white border-b-2 border-blue-600 text-blue-600" : "bg-slate-50 text-slate-400 hover:text-slate-600"}`}
              >
                Customer Details
              </button>
              <button
                onClick={() => setActiveTab("contact")}
                className={`flex-1 py-3 text-[11px] font-bold uppercase tracking-tighter transition-all ${activeTab === "contact" ? "bg-white border-b-2 border-blue-600 text-blue-600" : "bg-slate-50 text-slate-400 hover:text-slate-600"}`}
              >
                Contact Info
              </button>
            </div>

            <div className="p-4 min-h-[380px]">
              {activeTab === "details" ? (
                <div className="space-y-4 animate-in fade-in duration-300">
                  <div className="flex gap-2">
                    <label
                      className={`flex-1 flex items-center justify-center gap-2 cursor-pointer py-2 px-1 rounded-md transition-all ${customerType === "registered" ? "bg-white shadow-sm ring-1 ring-slate-200" : ""}`}
                    >
                      <input
                        type="radio"
                        name="customerType"
                        checked={customerType === "registered"}
                        onChange={() => setCustomerType("registered")}
                        className="w-3 h-3 text-blue-600 border-slate-300 focus:ring-blue-500"
                      />
                      <span
                        className={`text-[10px]  uppercase font-semibold tracking-tight ${customerType === "registered" ? "text-blue-600" : "text-slate-400"}`}
                      >
                        Registered
                      </span>
                    </label>

                    <label
                      className={`flex-1 flex items-center justify-center gap-2 cursor-pointer py-2 px-1 rounded-md transition-all ${customerType === "unregistered" ? "bg-white shadow-sm ring-1 ring-slate-200" : ""}`}
                    >
                      <input
                        type="radio"
                        name="customerType"
                        checked={customerType === "unregistered"}
                        onChange={() => setCustomerType("unregistered")}
                        className="w-3 h-3 text-blue-600 border-slate-300 focus:ring-blue-500"
                      />
                      <span
                        className={`text-[10px] uppercase font-semibold  tracking-tight ${customerType === "unregistered" ? "text-blue-600" : "text-slate-400"}`}
                      >
                        Un-Registered
                      </span>
                    </label>
                  </div>

                  {[
                    {
                      label: "Customer Name",
                      icon: <User size={12} />,
                      field: "customerName",
                    },
                    {
                      label: "Proj. Engineer",
                      icon: <Settings size={12} />,
                      field: "engineer",
                    },
                    {
                      label: "Contact Person",
                      icon: <Users size={12} />,
                      field: "contactPerson",
                    },
                    {
                      label: "Authority",
                      icon: <Shield size={12} />,
                      field: "authority",
                    },
                  ].map((field) => (
                    <div key={field.label}>
                      <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1">
                        {field.icon} {field.label}
                      </label>
                      <input
                        type="text"
                        className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-md text-sm p-2 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        placeholder={`Enter ${field.label}...`}
                        value={customerDetails[field.field]}
                        onChange={(e) =>
                          setCustomerDetails({
                            ...customerDetails,
                            [field.field]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in duration-300">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">
                      Phone Number
                    </label>
                    <div className="flex items-center gap-2 bg-slate-50 rounded-md px-2 border border-slate-200 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500">
                      <Phone size={14} className="text-slate-400" />
                      <input
                        type="text"
                        className="bg-transparent border-none focus:ring-0 text-sm w-full p-2 outline-none"
                        placeholder="+971 ..."
                        value={customerDetails.phone}
                        onChange={(e) =>
                          setCustomerDetails({
                            ...customerDetails,
                            phone: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">
                      Email Address
                    </label>
                    <div className="flex items-center gap-2 bg-slate-50 rounded-md px-2 border border-slate-200 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500">
                      <Mail size={14} className="text-slate-400" />
                      <input
                        type="email"
                        className="bg-transparent border-none focus:ring-0 text-sm w-full p-2 outline-none"
                        placeholder="client@company.com"
                        value={customerDetails.email}
                        onChange={(e) =>
                          setCustomerDetails({
                            ...customerDetails,
                            email: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase">
                      Location / Address
                    </label>
                    <div className="flex items-start gap-2 bg-slate-50 rounded-md px-2 border border-slate-200 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 py-1">
                      <MapPin size={14} className="text-slate-400 mt-2" />
                      <textarea
                        className="bg-transparent border-none focus:ring-0 text-sm w-full p-1 h-24 resize-none outline-none"
                        placeholder="Enter physical address..."
                        value={customerDetails.address}
                        onChange={(e) =>
                          setCustomerDetails({
                            ...customerDetails,
                            address: e.target.value,
                          })
                        }
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SECTION */}
        <div className="col-span-9 space-y-4">
          {/* Top Header Section */}
          <div className="bg-white p-4 rounded-xl border shadow-sm grid grid-cols-5 gap-4 items-end">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                Job Type
              </label>
              <select
                className="w-full bg-slate-50 border-slate-200 rounded-lg text-sm p-2 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500"
                value={quotationDetails.jobType}
                onChange={(e) =>
                  setQuotationDetails({
                    ...quotationDetails,
                    jobType: e.target.value,
                  })
                }
              >
                <option>Renovation</option>
                <option>New Construction</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                Enq No.
              </label>
              <input
                type="text"
                className="w-full bg-slate-50 border-slate-200 rounded-lg text-sm p-2 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500"
                placeholder="ENQ-2024-001"
                value={quotationDetails.enquiryNo}
                onChange={(e) =>
                  setQuotationDetails({
                    ...quotationDetails,
                    enquiryNo: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                Site Code
              </label>
              <input
                type="text"
                className="w-full bg-slate-50 border-slate-200 rounded-lg text-sm p-2 outline-none focus:bg-white focus:ring-2 focus:ring-blue-500"
                placeholder="LOC-DXB-01"
                value={quotationDetails.siteCode}
                onChange={(e) =>
                  setQuotationDetails({
                    ...quotationDetails,
                    siteCode: e.target.value,
                  })
                }
              />
            </div>
            <div className="text-right">
              <span className="block text-[10px] font-bold text-slate-400 uppercase leading-tight">
                Status
              </span>
              <span className="text-sm font-black text-blue-600 underline">
                DRAFT QUOTATION
              </span>
            </div>
            {/* <button
              onClick={() => fetchQuotationData(LeadIdFromParams?.leadId)}
              className="px-1 py-2 bg-green-600 text-center text-white rounded-lg font-bold hover:bg-green-700 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <Loader size={14} className="animate-spin mr-2" />
                  Loading...
                </span>
              ) : (
                "Load from API"
              )}
            </button> */}
          </div>

          {/* Main BOQ Table */}
          <ContractBoqTable
            items={items}
            setItems={setItems}
            selectedTemplates={selectedTemplates}
            handleAIRephrase={handleAIRephrase}
            handleUpdateItem={handleUpdateItem}
            setModalType={setModalType}
            setModalOpen={setModalOpen}
            addDefaultItems={addDefaultItems}
            setSelectedRowIndex={setSelectedRowIndex}
            loading={isLoading}
          />

          <div className="grid grid-cols-12 gap-4 ">
            <div className="col-span-8 bg-white border rounded-xl p-4 flex justify-between items-center shadow-sm">
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Actual Total
                </span>
                <span className="text-lg font-bold text-slate-700">
                  {totals.actualTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>

              <div className="h-8 w-[1px] bg-slate-100"></div>

              <div className="flex flex-col px-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  Tender Total
                </span>
                <span className="text-lg font-bold text-slate-700">
                  {totals.tenderTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>

              <div className="h-8 w-[1px] bg-slate-100"></div>

              <div className="flex flex-col pr-8">
                <span className="text-[10px] font-bold text-emerald-500 uppercase">
                  Expected Profit
                </span>
                <span className="text-lg font-bold text-emerald-600">
                  +{" "}
                  {totals.profit.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>

            <div className="col-span-4 flex items-center justify-end gap-3">
              <button className="flex p-4 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-50 transition-colors shadow-sm">
                Cancel
              </button>
              <button
                onClick={handleSaveQuotation}
                className="flex p-4 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-700 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                Save Quotation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Template Selection Modal */}
      <Modal
        isOpen={
          (modalType === "header" ||
            modalType === "footer" ||
            modalType === "emailPreview" ||
            modalType === "productList") &&
          modalOpen
        }
        onClose={() => {
          setModalOpen(false);
          setModalType(null);
        }}
        title={`Select a ${modalType} template`}
        size="xl"
      >
        {modalType === "emailPreview" ? (
          <EmailPreviewModal
            leadId={1679}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT={DEPARTMENT}
          />
        ) : (
          <ContractModal
            modalType={modalType}
            selectedTemplates={selectedTemplates}
            importView={importView}
            setImportView={setImportView}
            setModalOpen={setModalOpen}
            setSelectedTemplates={setSelectedTemplates}
          />
        )}
      </Modal>

      <Modal
        isOpen={showPdfPreview}
        onClose={() => {
          setShowPdfPreview(false);
          setPdfUrl(null);
        }}
        title="Quotation PDF Preview"
        size="xl"
      >
        <PdfViewer
          pdfUrl={pdfUrl}
          isGenerating={isGenerating}
          error={error}
          onDownload={handleDownload}
          onPrint={handlePrint}
          onRetry={generatePdf}
          quotationDetails={quotationDetails}
        />
      </Modal>
      <Modal
        isOpen={leadListModal}
        onClose={() => {
          setLeadListModal(false);
        }}
        title="Lead List"
        size="xl"
      >
        Lead List Modal
      </Modal>
    </div>
  );
};

export default ContractingQuotation;
